/**
 * Settings screen styles - V3 Design
 * Organized into card-based sections for better visual hierarchy
 */

import { StyleSheet, Dimensions } from 'react-native';
import { heightPercentageToDP as hp, widthPercentageToDP as wp } from 'react-native-responsive-screen';

import { fonts } from '../fonts';
import AppStyles from '../../AppStyles';

const screenWidth = Dimensions.get('window').width;
const isDesktop = screenWidth > 800;
const contentWidth = isDesktop ? '70%' : '100%';

const styles = StyleSheet.create({
  // Main content container
  innerContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
    backgroundColor: AppStyles.colorSet.whiteColor,
  },

  // Page title
  title: {
    marginBottom: 24,
    width: contentWidth,
    alignSelf: isDesktop ? 'center' : 'flex-start',
  },

  // Section card - groups related content
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    padding: 20,
    marginBottom: 16,
    width: contentWidth,
    alignSelf: isDesktop ? 'center' : 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },

  // Section header with title
  sectionHeader: {
    marginBottom: 16,
  },

  // Field group within a section
  fieldGroup: {
    marginBottom: 16,
  },

  // Last field group (no bottom margin)
  fieldGroupLast: {
    marginBottom: 0,
  },

  // Field label
  fieldLabel: {
    marginBottom: 6,
  },

  // Read-only field display - LEFT ALIGNED
  fieldValue: {
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },

  // Field value with icon
  fieldValueWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  // Profile row - horizontal layout for profile info
  profileRow: {
    flexDirection: 'row',
    gap: 12,
  },

  // Profile field - half width
  profileField: {
    flex: 1,
  },

  // Stats row - centered stat display
  statsRow: {
    alignItems: 'center',
    marginBottom: 12,
  },

  statCard: {
    alignItems: 'center',
    paddingVertical: 8,
  },

  // Email row - subtle centered display
  emailRow: {
    alignItems: 'center',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },

  // Primary button (gold)
  button: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    height: 52,
    paddingHorizontal: 24,
  },
  // Web hover/focus feedback — lift on the filled primary button
  buttonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },

  // Secondary button (subtle fill)
  buttonSecondary: {
    cursor: 'pointer',
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    height: 52,
    paddingHorizontal: 24,
  },
  buttonSecondaryHovered: {
    backgroundColor: '#EBEBEB',
  },

  // Container for button
  buttonContainer: {
    width: contentWidth,
    alignSelf: isDesktop ? 'center' : 'stretch',
  },

  // Disabled button state
  buttonDisabled: {
    backgroundColor: 'rgba(255, 196, 81, 0.5)',
    cursor: 'default',
  },

  // Text input field
  textInput: {
    textAlign: 'left',
    fontFamily: fonts.POPPINS.medium,
    fontSize: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: '#F8F8F8',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    color: AppStyles.colorSet.textPrimary,
  },

  // Separator line
  separator: {
    height: 1,
    backgroundColor: '#F0F0F0',
    marginVertical: 8,
  },

  // Section separator (larger)
  sectionSeparator: {
    height: 8,
    backgroundColor: 'transparent',
    marginVertical: 8,
  },

  // Version info at bottom
  versionContainer: {
    paddingVertical: 24,
    alignItems: 'center',
    width: contentWidth,
    alignSelf: 'center',
  },

  // Danger zone section
  dangerZone: {
    backgroundColor: 'rgba(239, 68, 68, 0.04)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.15)',
    padding: 20,
    marginBottom: 16,
    width: contentWidth,
    alignSelf: isDesktop ? 'center' : 'stretch',
  },

  dangerZoneHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },

  // Delete account text link
  deleteAccountLink: {
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },

  // Legacy - kept for compatibility
  textBox: {
    width: contentWidth,
    alignSelf: 'center',
  },

  // Recognition section - card style matching other Settings sections
  recognitionSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    padding: 20,
    marginBottom: 16,
    width: contentWidth,
    alignSelf: isDesktop ? 'center' : 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },

  // Quiet mode toggle row
  quietModeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },

  quietModeText: {
    flex: 1,
    marginRight: 12,
  },
});

export default styles;
